import React, { useEffect, useRef, useState, useCallback } from "react";
import { useWatch } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/24/outline";

import styles from "./smartImageUploader.module.css";
import { uploadImageRequest } from "../../http-request/uploadImageRequest";
import { SmartImageCard } from "./smartImageCard/SmartImageCard";

const MIN_SIZE = 500;

export default function SmartImageUploader({ fieldName, control, setValue, disabled = false }) {
  const rhfValues = useWatch({ control, name: fieldName });
  const [cards, setCards] = useState([]);
  const fileInputRef = useRef(null);
  const replaceIdRef = useRef(null);

  /* ---------- INITIAL SYNC ---------- */
  useEffect(() => {
    if (Array.isArray(rhfValues)) {
      const loadInitialCards = async () => {
        const promises = rhfValues.map(
          (url) =>
            new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                const error =
                  img.naturalWidth < MIN_SIZE || img.naturalHeight < MIN_SIZE
                    ? { type: "dimension", message: `Min size: ${MIN_SIZE}px` }
                    : null;
                resolve({
                  id: crypto.randomUUID(),
                  preview: url,
                  uploadedUrl: url,
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                  error,
                });
              };
              img.onerror = () =>
                resolve({
                  id: crypto.randomUUID(),
                  preview: null,
                  uploadedUrl: url,
                  width: null,
                  height: null,
                  error: { type: "invalid", message: "Image failed to load" },
                });
              img.src = url;
            })
        );

        const initialCards = await Promise.all(promises);
        setCards(initialCards);
      };

      loadInitialCards();
    }
  }, [rhfValues]);

  /* ---------- HELPERS ---------- */
  const syncToForm = useCallback(
    (nextCards) => {
      setValue(
        fieldName,
        nextCards.map((c) => c.uploadedUrl).filter(Boolean),
        { shouldDirty: true }
      );
    },
    [fieldName, setValue]
  );

  const openFilePicker = (replaceId = null) => {
    replaceIdRef.current = replaceId;
    fileInputRef.current.click();
  };

  /* ---------- FILE HANDLER ---------- */
  const handleFile = (file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    const img = new Image();
    const replacingId = replaceIdRef.current;

    img.onload = () => {
      const error =
        img.naturalWidth < MIN_SIZE || img.naturalHeight < MIN_SIZE
          ? { type: "dimension", message: `Min size: ${MIN_SIZE}px` }
          : null;

      const newCard = {
        id: replacingId || crypto.randomUUID(),
        file,
        preview,
        width: img.naturalWidth,
        height: img.naturalHeight,
        error,
        uploadedUrl: null,
      };

      setCards((prev) =>
        replacingId ? prev.map((c) => (c.id === replacingId ? newCard : c)) : [...prev, newCard]
      );
      replaceIdRef.current = null;
    };

    img.onerror = () => {
      const errorCard = {
        id: replacingId || crypto.randomUUID(),
        preview: null,
        error: { type: "invalid", message: "Image corrupted" },
        uploadedUrl: null,
      };
      setCards((prev) =>
        replacingId ? prev.map((c) => (c.id === replacingId ? errorCard : c)) : [...prev, errorCard]
      );
      replaceIdRef.current = null;
    };

    img.src = preview;
  };

  /* ---------- UPLOAD ---------- */
  const confirmUpload = async (card) => {
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, uploading: true } : c)));

    const res = await uploadImageRequest(card.file);
    const url = res?.fileUrl || res?.data?.fileUrl || res?.data?.url;

    setCards((prev) => {
      const next = prev.map((c) =>
        c.id === card.id ? { ...c, uploadedUrl: url, uploading: false } : c
      );
      syncToForm(next);
      return next;
    });
  };

  /* ---------- DELETE ---------- */
  const removeCard = (id) => {
    setCards((prev) => {
      const next = prev.filter((c) => c.id !== id);
      syncToForm(next);
      return next;
    });
  };

  const uploadedCount = cards.filter((c) => Boolean(c.uploadedUrl)).length;

  /* ---------- RENDER ---------- */
  return (
    <div className={styles.wrapper}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => handleFile(e.target.files[0])}
      />

      <div className={styles.grid}>
        {cards.map((card) => {
          const showConfirm = card.file && !card.error && !card.uploadedUrl && !card.uploading;
          return (
            <SmartImageCard
              key={card.id}
              card={card}
              onConfirmUpload={confirmUpload}
              onRemove={removeCard}
              onReplace={openFilePicker}
              showConfirm={showConfirm}
            />
          );
        })}

        {/* {!disabled && (
          <div className={styles.addCard} onClick={() => openFilePicker()}>
            <PlusIcon />
            <span>Add Image</span>
          </div>
        )} */}
      </div>

      <div className={styles.footer}>
        <span className={styles.counter}>
          {uploadedCount} image{uploadedCount !== 1 ? "s" : ""} added
        </span>
      </div>
    </div>
  );
}
